import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/PrefsContext";
import {
  PageHeader, PrimaryButton, TableShell, EmptyState, Modal, Field, Input, Textarea, SelectBox, Button, StatusBadge,
} from "@/components/ui-kit";
import { IconBtn } from "@/pages/Clients";
import { Plus, FileText, Pencil, Trash2, CheckCircle2, Trash } from "lucide-react";

const EMPTY = {
  client_id: "", customer_name: "", customer_email: "", customer_phone: "",
  address: "", city: "", state: "", zip: "",
  line_items: [{ service: "", description: "", qty: 1, price: 0 }],
  tax_rate: 0, discount: 0, status: "Draft", valid_until: "", notes: "",
};

export default function Estimates() {
  const [estimates, setEstimates] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({ tax_rate: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const { t } = useI18n();

  const load = () => api.get("/estimates").then(({ data }) => setEstimates(data));
  useEffect(() => {
    load();
    api.get("/clients").then(({ data }) => setClients(data));
    api.get("/services").then(({ data }) => setServices(data));
    api.get("/settings").then(({ data }) => setSettings(data));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setForm({ ...EMPTY, tax_rate: settings.tax_rate || 0, line_items: [{ service: "", description: "", qty: 1, price: 0 }] }); setEditing(null); setOpen(true); };
  const openEdit = (e) => { setForm({ ...EMPTY, ...e }); setEditing(e.id); setOpen(true); };

  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, client_id: cid, customer_name: c.name, customer_email: c.email, customer_phone: c.phone, address: c.address, city: c.city, state: c.state, zip: c.zip }));
    else set("client_id", "");
  };

  const setItem = (i, k, v) => {
    const items = [...form.line_items];
    items[i] = { ...items[i], [k]: v };
    if (k === "service") {
      const svc = services.find((s) => s.name === v);
      if (svc && !items[i].price) items[i].price = svc.base_price;
    }
    set("line_items", items);
  };
  const addItem = () => set("line_items", [...form.line_items, { service: "", description: "", qty: 1, price: 0 }]);
  const removeItem = (i) => set("line_items", form.line_items.filter((_, idx) => idx !== i));

  const subtotal = form.line_items.reduce((a, li) => a + Number(li.qty || 0) * Number(li.price || 0), 0);
  const tax = subtotal * Number(form.tax_rate || 0) / 100;
  const total = Math.max(0, subtotal + tax - Number(form.discount || 0));

  const save = async () => {
    if (!form.customer_name) return toast.error(t("Customer name is required"));
    const payload = {
      ...form, tax_rate: Number(form.tax_rate), discount: Number(form.discount),
      line_items: form.line_items.map((li) => ({ ...li, qty: Number(li.qty), price: Number(li.price) })),
    };
    try {
      if (editing) await api.put(`/estimates/${editing}`, payload);
      else await api.post("/estimates", payload);
      toast.success(editing ? t("Estimate updated") : t("Estimate created"));
      setOpen(false); load();
    } catch { toast.error("Could not save estimate"); }
  };

  const approve = async (e) => {
    if (!window.confirm(`Approve estimate ${e.estimate_number}? This creates a client and a scheduled cleaning.`)) return;
    try {
      await api.post(`/estimates/${e.id}/approve`, {});
      toast.success(t("Estimate approved — client & scheduled job created"));
      load();
    } catch { toast.error("Could not approve estimate"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this estimate?")) return;
    await api.delete(`/estimates/${id}`); toast.success(t("Estimate deleted")); load();
  };

  return (
    <div data-testid="estimates-page">
      <PageHeader title="Estimates" subtitle="Build professional estimates and convert approvals into scheduled cleanings."
        action={<PrimaryButton onClick={openNew} data-testid="add-estimate-button"><Plus size={16} /> {t("New Estimate")}</PrimaryButton>} />

      {estimates.length === 0 ? (
        <EmptyState icon={FileText} title="No estimates yet" subtitle="Create an estimate from scratch or convert a lead. Approved estimates auto-create a client and schedule a job."
          action={<PrimaryButton onClick={openNew}><Plus size={16} /> {t("New Estimate")}</PrimaryButton>} />
      ) : (
        <TableShell headers={["Estimate #", "Customer", "Total", "Status", "Actions"]} testid="estimates-table">
          {estimates.map((e) => (
            <tr key={e.id} className="border-b border-[#f4f0fa] hover:bg-[#faf8fd]">
              <td className="py-3.5 px-4 font-medium text-[#5A189A]">{e.estimate_number}</td>
              <td className="py-3.5 px-4 text-sm">{e.customer_name}</td>
              <td className="py-3.5 px-4 font-medium">{money(e.total)}</td>
              <td className="py-3.5 px-4"><StatusBadge status={e.status} /></td>
              <td className="py-3.5 px-4">
                <div className="flex items-center gap-1">
                  {e.status !== "Approved" && (
                    <IconBtn onClick={() => approve(e)} title="Approve & Schedule" testid={`approve-estimate-${e.id}`}><CheckCircle2 size={16} /></IconBtn>
                  )}
                  <IconBtn onClick={() => openEdit(e)} title="Edit"><Pencil size={16} /></IconBtn>
                  <IconBtn onClick={() => remove(e.id)} title="Delete" danger><Trash2 size={16} /></IconBtn>
                </div>
              </td>
            </tr>
          ))}
        </TableShell>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Estimate" : "New Estimate"} size="xl"
        footer={<><Button variant="outline" onClick={() => setOpen(false)}>{t("Cancel")}</Button><PrimaryButton onClick={save} data-testid="save-estimate-button">{t("Save Estimate")}</PrimaryButton></>}>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Existing Client"><SelectBox value={form.client_id} onChange={(e) => pickClient(e.target.value)} options={clients.map((c) => ({ value: c.id, label: c.name }))} placeholder="New customer / select client" /></Field>
            <Field label="Customer Name *"><Input value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} data-testid="estimate-customer-input" /></Field>
            <Field label="Email"><Input value={form.customer_email} onChange={(e) => set("customer_email", e.target.value)} /></Field>
            <Field label="Phone"><Input value={form.customer_phone} onChange={(e) => set("customer_phone", e.target.value)} /></Field>
            <Field label="Address" className="sm:col-span-2"><Input value={form.address} onChange={(e) => set("address", e.target.value)} /></Field>
            <Field label="City"><Input value={form.city} onChange={(e) => set("city", e.target.value)} /></Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="State"><Input value={form.state} onChange={(e) => set("state", e.target.value)} /></Field>
              <Field label="ZIP"><Input value={form.zip} onChange={(e) => set("zip", e.target.value)} /></Field>
            </div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-semibold text-[#121212]">{t("Line Items")}</label>
              <button onClick={addItem} className="text-[#5A189A] text-sm font-medium flex items-center gap-1 hover:text-[#D4AF37]"><Plus size={14} /> {t("Add Item")}</button>
            </div>
            <div className="space-y-2">
              {form.line_items.map((li, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <div className="col-span-4">
                    <SelectBox value={li.service} onChange={(e) => setItem(i, "service", e.target.value)} options={services.map((s) => s.name)} placeholder="Service" />
                  </div>
                  <div className="col-span-3"><Input placeholder="Description" value={li.description} onChange={(e) => setItem(i, "description", e.target.value)} /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Qty" value={li.qty} onChange={(e) => setItem(i, "qty", e.target.value)} /></div>
                  <div className="col-span-2"><Input type="number" placeholder="Price" value={li.price} onChange={(e) => setItem(i, "price", e.target.value)} /></div>
                  <div className="col-span-1 flex justify-center">
                    {form.line_items.length > 1 && <button onClick={() => removeItem(i)} className="text-red-400 hover:text-red-600"><Trash size={16} /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Tax Rate (%)"><Input type="number" value={form.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} /></Field>
            <Field label="Discount ($)"><Input type="number" value={form.discount} onChange={(e) => set("discount", e.target.value)} /></Field>
            <Field label="Status"><SelectBox value={form.status} onChange={(e) => set("status", e.target.value)} options={["Draft", "Sent", "Approved", "Declined"]} /></Field>
          </div>

          <div className="flex justify-end">
            <div className="w-60 text-sm space-y-1 bg-[#faf8fd] rounded-xl p-4">
              <div className="flex justify-between"><span className="text-[#52525B]">{t("Subtotal")}</span><span>{money(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#52525B]">{t("Tax")}</span><span>{money(tax)}</span></div>
              <div className="flex justify-between"><span className="text-[#52525B]">{t("Discount")}</span><span>- {money(form.discount)}</span></div>
              <div className="flex justify-between font-bold text-[#5A189A] pt-1 border-t border-[#e7ddf5] mt-1"><span>{t("Total")}</span><span>{money(total)}</span></div>
            </div>
          </div>

          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} /></Field>
        </div>
      </Modal>
    </div>
  );
}
